/**
 * API Client - Re-export of the main axios instance
 * This file provides backward compatibility for services importing from @/lib/apiClient
 *
 * The actual axios instance configuration is in @/services/api.ts
 * This re-export ensures all services can use the same configured instance
 */

import axios from "axios";
import { shouldBounceToInternalLogin } from "@/lib/authRedirect";
import { soltarDonoDoPush } from "@/lib/push/donoDoPush";
import { invalidarStatusSeForManutencao } from "@/lib/maintenanceResponseInterceptor";
import { tratar401 } from "@/lib/sessao/tratar401";

// Get API base URL from environment or default
const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add request interceptor to inject auth token and tenant headers
apiClient.interceptors.request.use(
  (config) => {
    // Get auth token from localStorage
    try {
      const authData = localStorage.getItem("seweb_auth");
      if (authData) {
        const parsed = JSON.parse(authData);
        if (parsed.access_token) {
          config.headers.Authorization = `Bearer ${parsed.access_token}`;
        }
      }
    } catch (error) {
      console.error("Failed to get auth token:", error);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Manutenção (28/09/2026, achado BAIXO da revisão de segurança, item
    // 10): esta é a SEGUNDA instância axios do projeto — a outra
    // (@/services/api) já tinha este interceptor; função compartilhada, não
    // duplicada.
    invalidarStatusSeForManutencao(error);

    if (error.response?.status === 401) {
      // O 401 tenta renovar a sessão e repetir o pedido UMA vez antes de derrubar a pessoa (`tratar401`). Só o desfecho "sair" faz o
      // que este ramo sempre fez; "esperar" (manutenção, rede) deixa a sessão como está e só rejeita.
      const desfecho = await tratar401(apiClient, error);
      if (desfecho.tipo === "repetida") return desfecho.resposta;
      if (desfecho.tipo === "sair") {
        // Handle unauthorized - token expired or invalid
        console.error("Unauthorized access - token expired, logging out");

        // Clear authentication data
        localStorage.removeItem("seweb_auth");
        // O 401 é o fim da sessão interna: a marca de dono do push do aparelho sai junto (se for do interno), senão o parceiro do portal
        // no mesmo navegador não toma o push e os avisos do colaborador seguem chegando ali (decisão de 12/10/2026).
        soltarDonoDoPush("interno");

        // Only redirect if not already on login page / not on a portal route.
        if (shouldBounceToInternalLogin(window.location.pathname)) {
          // Redirect to login page
          window.location.href = "/login";
        }
      }
    }
    return Promise.reject(error);
  }
);

export default apiClient;
